import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown } from "lucide-react";

function formatNumber(n) {
  if (!n) return "N/A";
  if (n >= 1e12) return "$" + (n / 1e12).toFixed(2) + "T";
  if (n >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  return "$" + n.toLocaleString();
}

function formatPrice(price) {
  if (!price) return "N/A";
  if (price < 1) return "$" + price.toFixed(6);
  return "$" + price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const COINS = [
  { symbol: "BTCUSDT", ticker: "BTC", name: "Bitcoin", slug: "bitcoin", logo: "https://assets.coingecko.com/coins/images/1/small/bitcoin.png" },
  { symbol: "ETHUSDT", ticker: "ETH", name: "Ethereum", slug: "ethereum", logo: "https://assets.coingecko.com/coins/images/279/small/ethereum.png" },
  { symbol: "BNBUSDT", ticker: "BNB", name: "BNB", slug: "bnb", logo: "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png" },
  { symbol: "XRPUSDT", ticker: "XRP", name: "XRP", slug: "xrp", logo: "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png" },
  { symbol: "SOLUSDT", ticker: "SOL", name: "Solana", slug: "solana", logo: "https://assets.coingecko.com/coins/images/4128/small/solana.png" },
  { symbol: "ADAUSDT", ticker: "ADA", name: "Cardano", slug: "cardano", logo: "https://assets.coingecko.com/coins/images/975/small/cardano.png" },
  { symbol: "DOGEUSDT", ticker: "DOGE", name: "Dogecoin", slug: "dogecoin", logo: "https://assets.coingecko.com/coins/images/5/small/dogecoin.png" },
  { symbol: "TRXUSDT", ticker: "TRX", name: "TRON", slug: "tron", logo: "https://assets.coingecko.com/coins/images/1094/small/tron-logo.png" },
  { symbol: "AVAXUSDT", ticker: "AVAX", name: "Avalanche", slug: "avalanche", logo: "https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png" },
  { symbol: "DOTUSDT", ticker: "DOT", name: "Polkadot", slug: "polkadot", logo: "https://assets.coingecko.com/coins/images/12171/small/polkadot.png" },
  { symbol: "LINKUSDT", ticker: "LINK", name: "Chainlink", slug: "chainlink", logo: "https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png" },
  { symbol: "LTCUSDT", ticker: "LTC", name: "Litecoin", slug: "litecoin", logo: "https://assets.coingecko.com/coins/images/2/small/litecoin.png" },
  { symbol: "POLUSDT", ticker: "POL", name: "Polygon", slug: "polygon", logo: "https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png" },
  { symbol: "UNIUSDT", ticker: "UNI", name: "Uniswap", slug: "uniswap", logo: "https://assets.coingecko.com/coins/images/12504/small/uniswap-uni.png" },
  { symbol: "NEARUSDT", ticker: "NEAR", name: "NEAR Protocol", slug: "near", logo: "https://assets.coingecko.com/coins/images/10365/small/near.jpg" },
  { symbol: "ATOMUSDT", ticker: "ATOM", name: "Cosmos", slug: "atom", logo: "https://assets.coingecko.com/coins/images/1481/small/cosmos_hub.png" },
  { symbol: "ARBUSDT", ticker: "ARB", name: "Arbitrum", slug: "arbitrum", logo: "https://assets.coingecko.com/coins/images/16547/small/photo_2023-03-29_21.47.00.jpeg" },
  { symbol: "OPUSDT", ticker: "OP", name: "Optimism", slug: "optimism", logo: "https://assets.coingecko.com/coins/images/25244/small/Optimism.png" },
  { symbol: "INJUSDT", ticker: "INJ", name: "Injective", slug: "injective", logo: "https://assets.coingecko.com/coins/images/12882/small/Secondary_Symbol.png" },
  { symbol: "SUIUSDT", ticker: "SUI", name: "Sui", slug: "sui", logo: "https://assets.coingecko.com/coins/images/26375/small/sui_asset.jpeg" },
];

export default function Market() {
  const [globalData, setGlobalData] = useState(null);
  const [fearGreed, setFearGreed] = useState(null);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch global market data
    fetch("https://api.coingecko.com/api/v3/global")
      .then((r) => r.json())
      .then((d) => setGlobalData(d.data))
      .catch(() => {});

    // Fetch Fear & Greed
    fetch("https://api.alternative.me/fng/")
      .then((r) => r.json())
      .then((d) => setFearGreed(d.data[0]))
      .catch(() => {});

    // Fetch all prices from Binance
    const symbols = COINS.map((c) => c.symbol);
    fetch("https://api.binance.com/api/v3/ticker/24hr")
      .then((r) => r.json())
      .then((data) => {
        const filtered = data
          .filter((d) => symbols.includes(d.symbol))
          .map((d) => {
            const coin = COINS.find((c) => c.symbol === d.symbol);
            return {
              ...coin,
              price: parseFloat(d.lastPrice),
              change: parseFloat(d.priceChangePercent),
              volume: parseFloat(d.quoteVolume),
            };
          });
        setPrices(filtered);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const btcDom = globalData?.market_cap_percentage?.btc;
  const ethDom = globalData?.market_cap_percentage?.eth;
  const totalMktCap = globalData?.total_market_cap?.usd;
  const totalVol = globalData?.total_volume?.usd;
  const mktCapChange = globalData?.market_cap_change_percentage_24h_usd;

  const fgi = fearGreed ? parseInt(fearGreed.value) : null;
  const fgiLabel = fearGreed?.value_classification || "N/A";
  const fgiColor = fgi === null ? "#71717a" : fgi >= 60 ? "#22c55e" : fgi >= 40 ? "#facc15" : "#ef4444";
  const fgiDeg = fgi !== null ? (fgi / 100) * 180 : 0;

  const sorted = [...prices].sort((a, b) => b.change - a.change);
  const gainers = sorted.slice(0, 5);
  const losers = sorted.slice(-5).reverse();

  return (
    <div className="pt-1 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="mb-8 mt-6">
          <span className="font-mono text-xs text-yellow-400 uppercase tracking-[0.3em]">/market</span>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-white tracking-tighter mt-2">
            Market Overview
          </h1>
          <p className="text-zinc-400 mt-2">Live crypto market data — dominance, sentiment and top movers.</p>
        </div>

        {/* Top Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0a0a0b] border border-white/10 p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Total Market Cap</div>
            <div className="font-display font-bold text-2xl text-white">{formatNumber(totalMktCap)}</div>
            {mktCapChange !== null && mktCapChange !== undefined && (
              <div className={"text-xs font-mono mt-1 " + (mktCapChange >= 0 ? "text-green-400" : "text-red-400")}>
                {mktCapChange >= 0 ? "+" : ""}{mktCapChange?.toFixed(2)}% 24h
              </div>
            )}
          </div>
          <div className="bg-[#0a0a0b] border border-white/10 p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">24h Volume</div>
            <div className="font-display font-bold text-2xl text-white">{formatNumber(totalVol)}</div>
          </div>
          <div className="bg-[#0a0a0b] border border-white/10 p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">BTC Dominance</div>
            <div className="font-display font-bold text-2xl text-yellow-400">{btcDom?.toFixed(1)}%</div>
            <div className="w-full bg-zinc-800 h-1.5 mt-2">
              <div className="bg-yellow-400 h-1.5" style={{ width: (btcDom || 0) + "%" }} />
            </div>
          </div>
          <div className="bg-[#0a0a0b] border border-white/10 p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">ETH Dominance</div>
            <div className="font-display font-bold text-2xl text-blue-400">{ethDom?.toFixed(1)}%</div>
            <div className="w-full bg-zinc-800 h-1.5 mt-2">
              <div className="bg-blue-400 h-1.5" style={{ width: (ethDom || 0) + "%" }} />
            </div>
          </div>
        </div>

        {/* Fear & Greed + BTC Dominance Visual */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">

          {/* Fear & Greed Gauge */}
          <div className="bg-[#0a0a0b] border border-white/10 p-6 flex flex-col items-center">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Fear & Greed Index</div>
            <div className="relative w-48 h-24 overflow-hidden">
              {/* Gauge background */}
              <svg viewBox="0 0 200 100" className="w-full">
                {/* Background arc */}
                <path d="M 10 100 A 90 90 0 0 1 190 100" fill="none" stroke="#1a1a1a" strokeWidth="20" />
                {/* Red zone */}
                <path d="M 10 100 A 90 90 0 0 1 55 28" fill="none" stroke="#ef4444" strokeWidth="20" opacity="0.6" />
                {/* Orange zone */}
                <path d="M 55 28 A 90 90 0 0 1 100 10" fill="none" stroke="#f97316" strokeWidth="20" opacity="0.6" />
                {/* Yellow zone */}
                <path d="M 100 10 A 90 90 0 0 1 145 28" fill="none" stroke="#facc15" strokeWidth="20" opacity="0.6" />
                {/* Green zone */}
                <path d="M 145 28 A 90 90 0 0 1 190 100" fill="none" stroke="#22c55e" strokeWidth="20" opacity="0.6" />
                {/* Needle */}
                <line
                  x1="100" y1="100"
                  x2={100 + 70 * Math.cos(Math.PI - (fgiDeg * Math.PI / 180))}
                  y2={100 - 70 * Math.sin(Math.PI - (fgiDeg * Math.PI / 180))}
                  stroke="white" strokeWidth="3" strokeLinecap="round"
                />
                <circle cx="100" cy="100" r="6" fill="white" />
              </svg>
            </div>
            <div className="font-display font-bold text-5xl mt-2" style={{ color: fgiColor }}>
              {fgi !== null ? fgi : "—"}
            </div>
            <div className="font-mono text-sm mt-1" style={{ color: fgiColor }}>{fgiLabel}</div>
            <div className="flex justify-between w-full mt-4 text-xs font-mono text-zinc-500">
              <span>Extreme Fear</span>
              <span>Neutral</span>
              <span>Extreme Greed</span>
            </div>
          </div>

          {/* Dominance Chart */}
          <div className="bg-[#0a0a0b] border border-white/10 p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">Market Dominance</div>
            {globalData && (
              <div className="space-y-4">
                {[
                  { label: "Bitcoin", key: "btc", color: "bg-yellow-400" },
                  { label: "Ethereum", key: "eth", color: "bg-blue-400" },
                  { label: "BNB", key: "bnb", color: "bg-orange-400" },
                  { label: "XRP", key: "xrp", color: "bg-purple-400" },
                  { label: "Others", key: null, color: "bg-zinc-600" },
                ].map((item) => {
                  const pct = item.key
                    ? globalData.market_cap_percentage?.[item.key] || 0
                    : 100 - Object.values(globalData.market_cap_percentage || {}).slice(0, 4).reduce((a, b) => a + b, 0);
                  return (
                    <div key={item.label}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-zinc-300 font-medium">{item.label}</span>
                        <span className="font-mono text-white font-bold">{pct.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-zinc-800 h-2">
                        <div className={"h-2 transition-all duration-700 " + item.color} style={{ width: pct + "%" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Top Gainers & Losers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">

          {/* Gainers */}
          <div className="bg-[#0a0a0b] border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-400">Top Gainers 24h</h3>
            </div>
            <div className="space-y-3">
              {gainers.map((coin) => (
                <Link key={coin.symbol} to={"/coins/" + coin.slug} className="flex items-center justify-between hover:bg-white/5 px-2 py-2 transition group">
                  <div className="flex items-center gap-3">
                    <img src={coin.logo} alt={coin.name} className="w-7 h-7 rounded-full" onError={(e) => e.target.style.display = "none"} />
                    <div>
                      <div className="font-display font-bold text-white text-sm group-hover:text-yellow-400 transition">{coin.name}</div>
                      <div className="text-xs font-mono text-zinc-500">{coin.ticker}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm text-white">{formatPrice(coin.price)}</div>
                    <div className="font-mono text-xs font-bold text-green-400">+{coin.change.toFixed(2)}%</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Losers */}
          <div className="bg-[#0a0a0b] border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown className="w-4 h-4 text-red-400" />
              <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-400">Top Losers 24h</h3>
            </div>
            <div className="space-y-3">
              {losers.map((coin) => (
                <Link key={coin.symbol} to={"/coins/" + coin.slug} className="flex items-center justify-between hover:bg-white/5 px-2 py-2 transition group">
                  <div className="flex items-center gap-3">
                    <img src={coin.logo} alt={coin.name} className="w-7 h-7 rounded-full" onError={(e) => e.target.style.display = "none"} />
                    <div>
                      <div className="font-display font-bold text-white text-sm group-hover:text-yellow-400 transition">{coin.name}</div>
                      <div className="text-xs font-mono text-zinc-500">{coin.ticker}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm text-white">{formatPrice(coin.price)}</div>
                    <div className="font-mono text-xs font-bold text-red-400">{coin.change.toFixed(2)}%</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Top 20 Coins Table */}
        <div className="bg-[#0a0a0b] border border-white/10 overflow-hidden">
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <h3 className="font-display font-bold text-xl text-white">Top 20 Coins</h3>
            <Link to="/coins" className="text-xs font-mono uppercase tracking-widest text-yellow-400 hover:underline">
              View all 100 →
            </Link>
          </div>
          <div className="grid grid-cols-12 px-6 py-3 border-b border-white/10 text-xs font-mono uppercase tracking-widests text-zinc-500">
            <div className="col-span-1">#</div>
            <div className="col-span-5 md:col-span-4">Name</div>
            <div className="col-span-3 text-right">Price</div>
            <div className="col-span-3 text-right">24h</div>
          </div>
          {loading ? (
            <div className="p-8 text-center text-zinc-500 font-mono text-sm">Loading...</div>
          ) : (
            prices.slice(0, 20).map((coin, index) => (
              <Link
                key={coin.symbol}
                to={"/coins/" + coin.slug}
                className="grid grid-cols-12 px-6 py-4 border-b border-white/5 hover:bg-white/5 transition items-center group"
              >
                <div className="col-span-1 font-mono text-sm text-zinc-500">{index + 1}</div>
                <div className="col-span-5 md:col-span-4 flex items-center gap-3">
                  <img src={coin.logo} alt={coin.name} className="w-7 h-7 rounded-full" onError={(e) => e.target.style.display = "none"} />
                  <div>
                    <div className="font-display font-bold text-white group-hover:text-yellow-400 transition text-sm">{coin.name}</div>
                    <div className="text-xs font-mono text-zinc-500">{coin.ticker}</div>
                  </div>
                </div>
                <div className="col-span-3 text-right font-display font-bold text-white text-sm">
                  {formatPrice(coin.price)}
                </div>
                <div className={"col-span-3 text-right font-mono text-sm font-bold " + (coin.change >= 0 ? "text-green-400" : "text-red-400")}>
                  {coin.change >= 0 ? "+" : ""}{coin.change.toFixed(2)}%
                </div>
              </Link>
            ))
          )}
        </div>

      </div>
    </div>
  );
}